// A label layout is a flat list of positioned elements. Most kinds (image,
// name, sku, price, barcode, qrcode) are singletons — the editor toggles them
// on/off like a checkbox — but "text" and "icon" are repeatable: each click
// of "Add text box" / a mark button appends a new element with its own id, so
// a label can carry any number of custom text boxes or compliance marks.
export type LabelElementKind =
  | "image"
  | "name"
  | "sku"
  | "price"
  | "batch"
  | "barcode"
  | "qrcode"
  | "text"
  | "importer"
  | "icon";

// Built-in compliance/regulatory mark graphics, plus the MAXCEI brand
// wordmark (see marks.tsx). Not a legal certification tool — verify
// proportions/usage against the official specs (especially the CE mark,
// which EU law regulates the exact shape of) before relying on these for
// real regulatory labeling.
export const MARK_IDS = [
  "ce",
  "ce-red",
  "ukca",
  "weee",
  "rohs",
  "recycle",
  "reach",
  // UN GHS / EU CLP hazard pictograms (see ghsPictograms.ts).
  "ghs01",
  "ghs02",
  "ghs03",
  "ghs04",
  "ghs05",
  "ghs06",
  "ghs07",
  "ghs08",
  "ghs09",
  "brand",
  "brand-stacked",
] as const;
export type MarkId = (typeof MARK_IDS)[number];

export const SINGLETON_ELEMENT_KINDS: LabelElementKind[] = [
  "name",
  "sku",
  "price",
  "batch",
  "barcode",
  "qrcode",
  "importer",
];

// Kinds whose box renders text (as opposed to an image/barcode/qrcode
// graphic) and therefore accepts style + an optional "Label:" prefix.
export const TEXT_ELEMENT_KINDS: LabelElementKind[] = [
  "name",
  "sku",
  "price",
  "batch",
  "text",
  "importer",
];

export interface LabelBox {
  // All in percent of the label's own width/height (0-100), so one layout
  // scales correctly across sheet, roll and custom label sizes.
  x: number;
  y: number;
  width: number;
  height: number;
}

export type LabelTextAlign = "left" | "center" | "right";

export interface LabelTextStyle {
  // Points (pt) — a physical, print-accurate unit, independent of screen zoom.
  fontSizePt?: number;
  bold?: boolean;
  align?: LabelTextAlign;
  // CSS color, e.g. "#000000".
  color?: string;
}

export interface LabelElement {
  id: string;
  kind: LabelElementKind;
  box: LabelBox;
  // "text" kind only.
  content?: string;
  // sku/price only — prefixes the value with e.g. "SKU: ".
  showLabel?: boolean;
  // name/sku/price/text/importer only.
  style?: LabelTextStyle;
  // barcode kind only — JsBarcode's own displayValue beneath the bars.
  barcodeShowText?: boolean;
  // icon kind only — which built-in mark graphic to render.
  markId?: MarkId;
  // importer kind only — the importer's street/postal address (`content`
  // holds the company name, shown under an "Importador:" label).
  importerAddress?: string;
  // importer kind only — which of the four sub-fields to include. Name is
  // `content`, address is `importerAddress`; email/phone are the fixed
  // company contact constants below rather than per-template text, since
  // they're the same on every label. All default to shown (undefined = true).
  importerFields?: { name?: boolean; address?: boolean; email?: boolean; phone?: boolean };
  // Zero-based product/variant image index. Older layouts omit this and use
  // the first image.
  imageIndex?: number;
}

export interface LabelLayout {
  elements: LabelElement[];
  // How the template prints by default (paper, roll layout…), stored by
  // "Use as this template's default" — see printSetup.ts, which validates it.
  printSetup?: unknown;
}

export const DEFAULT_TEXT_STYLE: LabelTextStyle = {
  fontSizePt: 9,
  bold: false,
  align: "left",
  color: "#000000",
};

const createId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `el-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const DEFAULT_BOX: LabelBox = { x: 10, y: 10, width: 30, height: 20 };
const MARK_BOX: LabelBox = { x: 10, y: 10, width: 15, height: 15 };

const DEFAULT_BOX_BY_KIND: Record<LabelElementKind, LabelBox> = {
  image: { x: 2, y: 15, width: 18, height: 70 },
  name: { x: 22, y: 6, width: 30, height: 32 },
  sku: { x: 22, y: 40, width: 30, height: 20 },
  batch: { x: 22, y: 60, width: 30, height: 14 },
  price: { x: 22, y: 76, width: 30, height: 20 },
  barcode: { x: 54, y: 15, width: 44, height: 70 },
  qrcode: { x: 54, y: 15, width: 30, height: 70 },
  text: DEFAULT_BOX,
  importer: DEFAULT_BOX,
  icon: MARK_BOX,
};

// A reasonable starting point for a new/unedited label — image on the left,
// text in the middle, barcode on the right. Purely a starting point: every
// box is meant to be dragged and resized.
export const DEFAULT_LABEL_LAYOUT: LabelLayout = {
  elements: [
    { id: "image", kind: "image", box: DEFAULT_BOX_BY_KIND.image },
    { id: "name", kind: "name", box: DEFAULT_BOX_BY_KIND.name, style: DEFAULT_TEXT_STYLE },
    { id: "sku", kind: "sku", box: DEFAULT_BOX_BY_KIND.sku, style: DEFAULT_TEXT_STYLE },
    { id: "price", kind: "price", box: DEFAULT_BOX_BY_KIND.price, style: DEFAULT_TEXT_STYLE },
    {
      id: "barcode",
      kind: "barcode",
      box: DEFAULT_BOX_BY_KIND.barcode,
      barcodeShowText: true,
    },
  ],
};

export const addSingletonElement = (layout: LabelLayout, kind: LabelElementKind): LabelLayout => {
  if (layout.elements.some(element => element.kind === kind)) {
    return layout;
  }

  const defaultFromStarter = DEFAULT_LABEL_LAYOUT.elements.find(element => element.kind === kind);
  const element: LabelElement = {
    id: createId(),
    kind,
    box: DEFAULT_BOX_BY_KIND[kind],
    ...(TEXT_ELEMENT_KINDS.includes(kind) ? { style: DEFAULT_TEXT_STYLE } : {}),
    ...(kind === "barcode" ? { barcodeShowText: defaultFromStarter?.barcodeShowText ?? true } : {}),
    ...(kind === "importer"
      ? {
          content: "",
          importerAddress: "",
          importerFields: { name: true, address: true, email: true, phone: true },
        }
      : {}),
  };

  return { elements: [...layout.elements, element] };
};

export const addTextBox = (layout: LabelLayout): LabelLayout => ({
  elements: [
    ...layout.elements,
    {
      id: createId(),
      kind: "text",
      box: DEFAULT_BOX,
      content: "",
      style: DEFAULT_TEXT_STYLE,
    },
  ],
});

export const addImageElement = (layout: LabelLayout): LabelLayout => {
  const imageElements = layout.elements.filter(element => element.kind === "image");
  const usedImageIndexes = new Set(imageElements.map(element => element.imageIndex ?? 0));
  let imageIndex = 0;

  while (usedImageIndexes.has(imageIndex)) {
    imageIndex += 1;
  }

  return {
    elements: [
      ...layout.elements,
      {
        id: createId(),
        kind: "image",
        box: {
          ...DEFAULT_BOX_BY_KIND.image,
          x: Math.min(100 - DEFAULT_BOX_BY_KIND.image.width, 2 + imageElements.length * 4),
        },
        imageIndex,
      },
    ],
  };
};

export const addMarkElement = (layout: LabelLayout, markId: MarkId): LabelLayout => {
  // New marks default to the size of the most recently added one instead of
  // the generic MARK_BOX — compliance icons are almost always meant to read
  // as a uniform row, so matching whatever size is already on the label
  // saves having to manually resize every icon added after the first.
  // Offset to the right of it (clamped to the label) so it doesn't land
  // exactly on top of the existing icon, invisible until dragged.
  const existingIcons = layout.elements.filter(element => element.kind === "icon");
  const referenceIcon = existingIcons[existingIcons.length - 1];
  const box: LabelBox = referenceIcon
    ? {
        width: referenceIcon.box.width,
        height: referenceIcon.box.height,
        x: Math.min(
          100 - referenceIcon.box.width,
          referenceIcon.box.x + referenceIcon.box.width + 2,
        ),
        y: referenceIcon.box.y,
      }
    : MARK_BOX;

  return {
    elements: [
      ...layout.elements,
      {
        id: createId(),
        kind: "icon",
        box,
        markId,
      },
    ],
  };
};

export const removeElement = (layout: LabelLayout, id: string): LabelLayout => ({
  elements: layout.elements.filter(element => element.id !== id),
});

export type LabelResizeMode = "scale" | "keep";

// Re-lays out a layout for a new physical label size. Boxes are stored as
// percent of the label, so on their own they always scale with it — which
// is "scale" mode's box behavior for free, plus text sizes multiplied by the
// overall scale factor so type grows/shrinks like the images and marks do.
// "keep" instead holds every box and font at its current physical (mm) size
// and only moves things: each box stays pinned to whichever edge it was
// nearer to (so a bottom icon row stays at the bottom, a header stays on
// top) and is clamped back onto the label if there's now less room. Always
// computed from the layout/size the resize started from (not step by step)
// so typing "130" via 1 → 13 → 130 doesn't compound clamping losses.
export const resizeLayoutForLabelSize = (
  layout: LabelLayout,
  mode: LabelResizeMode,
  prevWidthMm: number,
  prevHeightMm: number,
  nextWidthMm: number,
  nextHeightMm: number,
): LabelLayout => {
  if (mode === "scale") {
    const factor = Math.sqrt((nextWidthMm / prevWidthMm) * (nextHeightMm / prevHeightMm));

    return {
      elements: layout.elements.map(element =>
        TEXT_ELEMENT_KINDS.includes(element.kind)
          ? {
              ...element,
              style: {
                ...DEFAULT_TEXT_STYLE,
                ...element.style,
                fontSizePt:
                  Math.round(
                    Math.max(
                      1,
                      (element.style?.fontSizePt ?? DEFAULT_TEXT_STYLE.fontSizePt ?? 9) * factor,
                    ) * 100,
                  ) / 100,
              },
            }
          : element,
      ),
    };
  }

  const pinAxis = (
    positionPercent: number,
    sizePercent: number,
    prevMm: number,
    nextMm: number,
  ) => {
    const sizeMm = Math.min((sizePercent / 100) * prevMm, nextMm);
    const positionMm = (positionPercent / 100) * prevMm;
    const nearerStart = positionMm + sizeMm / 2 <= prevMm / 2;
    const pinnedMm = nearerStart ? positionMm : nextMm - (prevMm - positionMm);
    const clampedMm = Math.min(Math.max(pinnedMm, 0), nextMm - sizeMm);

    return {
      position: (clampedMm / nextMm) * 100,
      size: (sizeMm / nextMm) * 100,
    };
  };

  return {
    elements: layout.elements.map(element => {
      const horizontal = pinAxis(element.box.x, element.box.width, prevWidthMm, nextWidthMm);
      const vertical = pinAxis(element.box.y, element.box.height, prevHeightMm, nextHeightMm);

      return {
        ...element,
        box: {
          x: horizontal.position,
          y: vertical.position,
          width: horizontal.size,
          height: vertical.size,
        },
      };
    }),
  };
};

export const updateElement = (
  layout: LabelLayout,
  id: string,
  patch: Partial<Omit<LabelElement, "id" | "kind">>,
): LabelLayout => ({
  elements: layout.elements.map(element =>
    element.id === id ? { ...element, ...patch } : element,
  ),
});

// Pre-array-model templates saved a box per fixed field key directly on the
// layout object (e.g. `{ name: {...}, sku: {...}, barcodeShowText: true }`).
// Every field key doubled as a stable, unique element id, so migrating is
// lossless: one element per present key, ordered the same way the editor
// always rendered them.
const LEGACY_FIELD_ORDER: LabelElementKind[] = ["image", "name", "sku", "price", "barcode"];

interface LegacyLabelLayout {
  image?: LabelBox;
  name?: LabelBox;
  sku?: LabelBox;
  price?: LabelBox;
  barcode?: LabelBox;
  barcodeShowText?: boolean;
}

const isLegacyLayout = (value: object): value is LegacyLabelLayout => !("elements" in value);

const migrateLegacyLayout = (legacy: LegacyLabelLayout): LabelLayout => ({
  elements: LEGACY_FIELD_ORDER.filter(kind => legacy[kind as keyof LegacyLabelLayout]).map(
    kind => ({
      id: kind,
      kind,
      box: legacy[kind as keyof LegacyLabelLayout] as LabelBox,
      ...(TEXT_ELEMENT_KINDS.includes(kind) ? { style: DEFAULT_TEXT_STYLE } : {}),
      ...(kind === "barcode" ? { barcodeShowText: legacy.barcodeShowText ?? true } : {}),
    }),
  ),
});

// Fields that print the product's own data. A typed override on one of these
// is a tweak for the print at hand: saved into a template it would print the
// same name/SKU/price/batch on every product (a name override typed while
// the editor showed one sample product was being saved that way).
const PRODUCT_DATA_KINDS = new Set<LabelElement["kind"]>(["name", "sku", "price", "batch"]);

export const withoutProductDataOverrides = (layout: LabelLayout): LabelLayout =>
  layout.elements.some(
    element => PRODUCT_DATA_KINDS.has(element.kind) && element.content !== undefined,
  )
    ? {
        ...layout,
        elements: layout.elements.map(element => {
          if (!PRODUCT_DATA_KINDS.has(element.kind) || element.content === undefined) {
            return element;
          }

          const { content: _override, ...rest } = element;

          return rest;
        }),
      }
    : layout;

// Saved templates go through withoutProductDataOverrides, which also repairs
// templates saved before overrides were kept out of them.
export const parseLabelLayout = (raw: unknown): LabelLayout =>
  withoutProductDataOverrides(parseRawLabelLayout(raw));

const parseRawLabelLayout = (raw: unknown): LabelLayout => {
  const value =
    typeof raw === "string"
      ? (() => {
          try {
            return JSON.parse(raw) as unknown;
          } catch {
            return null;
          }
        })()
      : raw;

  if (!value || typeof value !== "object") {
    return DEFAULT_LABEL_LAYOUT;
  }

  if (isLegacyLayout(value)) {
    return migrateLegacyLayout(value as LegacyLabelLayout);
  }

  const layout = value as LabelLayout;

  return Array.isArray(layout.elements) ? layout : DEFAULT_LABEL_LAYOUT;
};

// Marks whose presence depends on the product (CE/RoHS/WEEE only apply to
// electrical equipment, hazard pictograms to chemicals, etc.) — the brand
// marks always print.
const PRODUCT_MARK_IDS: MarkId[] = MARK_IDS.filter(id => id !== "brand" && id !== "brand-stacked");

// Parses the product's `label_marks` metadata value ("ce,weee,rohs", "" =
// none), keeping its order. Null means the product has no rule, so the
// template's marks print unchanged.
export const parseLabelMarks = (raw: string | null | undefined): MarkId[] | null => {
  if (raw === null || raw === undefined) {
    return null;
  }

  const ids = raw
    .split(",")
    .map(id => id.trim())
    .filter((id): id is MarkId => PRODUCT_MARK_IDS.includes(id as MarkId));

  return Array.from(new Set(ids));
};

const isProductMark = (element: LabelElement) =>
  element.kind === "icon" && PRODUCT_MARK_IDS.includes(element.markId ?? "ce");

const byReadingOrder = (a: LabelBox, b: LabelBox) => a.y - b.y || a.x - b.x;

// The template's compliance marks act as the label's mark row: their boxes
// are slots that get filled, in order, with the marks this product needs —
// so a wheel spacer prints none, a lamp prints CE/WEEE/RoHS/recycle and a
// spray prints its hazard pictograms, all from the same template. A mark the
// template already has keeps its element (and color); others are added. If
// the product needs more marks than the template has slots, the row is
// extended with the same spacing while it still fits on the label.
export const applyProductMarks = (
  layout: LabelLayout,
  productMarks: MarkId[] | null,
): LabelLayout => {
  if (!productMarks) {
    return layout;
  }

  const templateMarks = layout.elements.filter(isProductMark);

  if (templateMarks.length === 0) {
    return layout;
  }

  const slots = templateMarks.map(element => element.box).sort(byReadingOrder);

  while (slots.length < productMarks.length) {
    const last = slots[slots.length - 1];
    const previous = slots[slots.length - 2];
    const step = previous && previous.y === last.y ? last.x - previous.x : last.width * 1.3;
    const next = { ...last, x: last.x + step };

    if (next.x + next.width > 100) {
      break;
    }

    slots.push(next);
  }

  const unused = [...templateMarks];
  const placed = productMarks.slice(0, slots.length).map((markId, index): LabelElement => {
    const matchIndex = unused.findIndex(
      element => element.markId === markId || (markId === "ce" && element.markId === "ce-red"),
    );

    if (matchIndex >= 0) {
      const [element] = unused.splice(matchIndex, 1);

      return { ...element, box: slots[index] };
    }

    return { id: `product-mark-${markId}`, kind: "icon", markId, box: slots[index] };
  });

  return {
    ...layout,
    elements: [...layout.elements.filter(element => !isProductMark(element)), ...placed],
  };
};

// Labels this short print text so small that anything below the SKU line's
// size is hard to read (e.g. the importer block on a 50×30 roll label).
const SMALL_LABEL_MAX_HEIGHT_MM = 30;

/**
 * The smallest font size (pt) any text on this label may print at: on small
 * labels, the template's SKU size; otherwise no minimum. Larger labels keep
 * their designed sizes, since their boxes were sized for smaller text.
 */
export const textFloorPt = (layout: LabelLayout, heightMm: number): number | undefined => {
  if (heightMm > SMALL_LABEL_MAX_HEIGHT_MM) {
    return undefined;
  }

  const sku = layout.elements.find(element => element.kind === "sku");

  return sku ? (sku.style?.fontSizePt ?? DEFAULT_TEXT_STYLE.fontSizePt) : undefined;
};
